import { useEffect } from "react";
import type {
  UsageScreenName,
  UsageFeatureName,
  WebUsageEventName,
} from "@juro/shared";
import { authHeaders } from "../api.js";

/**
 * 利用状況計測ヘルパー（設計書: usage-analytics-admin-console.md §2-5）。
 *
 * - イベントはページ内キューに溜め、まとめて POST /api/events に非同期送信する
 * - 送信は fetch keepalive（認証ヘッダーが必要なため sendBeacon は使わない）
 * - **計測がプロダクト動作を絶対に阻害しない**: 失敗は握りつぶす。await でUIを止めない
 * - properties に顧客名・金額等の PII を入れない（カタログの strict スキーマがサーバー側でも拒否する）
 */

type QueuedEvent = {
  name: WebUsageEventName;
  properties?: Record<string, unknown>;
  occurred_at: string;
};

const MAX_BATCH = 20;
const FLUSH_INTERVAL_MS = 30_000;
const FLUSH_THRESHOLD = 10;

let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function flush(): void {
  if (queue.length === 0) return;
  const events = queue.slice(0, MAX_BATCH);
  queue = queue.slice(MAX_BATCH);
  void (async () => {
    try {
      const headers = await authHeaders({ "Content-Type": "application/json" });
      await fetch("/api/events", {
        method: "POST",
        keepalive: true,
        headers,
        body: JSON.stringify({ events }),
      });
    } catch {
      // 計測はベストエフォート
    }
  })();
  if (queue.length > 0) flush();
}

function scheduleFlush(): void {
  if (flushTimer !== null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

export function track(name: WebUsageEventName, properties?: Record<string, unknown>): void {
  try {
    queue.push({ name, properties, occurred_at: new Date().toISOString() });
    if (queue.length >= FLUSH_THRESHOLD) {
      flush();
    } else {
      scheduleFlush();
    }
  } catch {
    // 計測はベストエフォート
  }
}

// タブ非表示・離脱時に残りを送る（keepalive によりアンロード中も送信が継続される）
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
  window.addEventListener("pagehide", () => flush());
}

export function trackScreenView(screen: UsageScreenName): void {
  track("screen_view", { screen });
}

export function trackFeatureAbandoned(feature: UsageFeatureName, step?: string): void {
  track("feature_abandoned", step ? { feature, step } : { feature });
}

export function trackSearchUsed(screen: UsageScreenName, hasResult: boolean): void {
  track("search_used", { screen, has_result: hasResult });
}

/** error_name のみ送る（メッセージ本文は PII 混入防止のため送らない） */
export function trackClientError(errorName: string, screen?: UsageScreenName): void {
  track("client_error", screen ? { screen, error_name: errorName } : { error_name: errorName });
}

// ── テンプレート作成方式の受け渡し ─────────────────────────────────────────
// 空/AI/PDF取込フローは editorStore の draft 経由で共通エディタに合流するため、
// 作成方式（template_created の method）をモジュール変数で保存→保存時に消費する。
// リロードで消えた場合はサーバー側が 'blank' として記録する（分析上許容）。

type TemplateDraftOrigin = "blank" | "ai" | "pdf_import";
let pendingTemplateDraftOrigin: TemplateDraftOrigin | null = null;

export function setTemplateDraftOrigin(origin: TemplateDraftOrigin): void {
  pendingTemplateDraftOrigin = origin;
}

export function consumeTemplateDraftOrigin(): TemplateDraftOrigin | null {
  const value = pendingTemplateDraftOrigin;
  pendingTemplateDraftOrigin = null;
  return value;
}

/** 画面表示イベント（マウント時に1回） */
export function useScreenView(screen: UsageScreenName): void {
  useEffect(() => {
    trackScreenView(screen);
  }, [screen]);
}
